import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useMutation } from '@apollo/client';

import { SiteContext } from '../../../../utils/context/SiteContext';
import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Container } from '../../../layout/Container';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';
import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { Hero } from '../../../ui/hero/Index';
import { GENERAL_VALUATION_MUTATION } from '../../../../../client/__graphql__/mutations/valuation';
import { createGTMEvent } from '../../../../utils/tracking/gtm';
import {
  LeadType,
  PageType
} from '../../../../__types__/generated/globalTypes';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

interface IRoute {
  office: string;
  path: string;
}

const FritidsArtikkel3: React.FC<RouteComponentProps<IRoute>> = ({
  location,
  match
}) => {
  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  return (
    <>
      <Seo
        title="Spesialister for enhver pris - PrivatMegleren"
        description="Livet på hytta tilbringes gjerne i roligere tempo enn hverdagslivet
        hjemme. Sammenlignet med eiendomsmarkedet er det også ofte mindre
        hektisk ved salg av fritidseiendom, ettersom omsetningen av hytter i
        større grad følger årstidene. Å ta seg god tid kan lønne seg."
        url={location.pathname}
      />
      <Hero
        imageUrl="https://cdn.reeltime.no/pm_assets/kampanjer/leveranse/fritid/Toppbilde_3.jpg"
        opacity={0.4}
        className="content-height flex padding-top-50"
        alt="bilde"
      >
        <HeroContent style={pageFadeIn}>
          <Heading tag="h1" center={true}>
            Spesialister for &nbsp; enhver pris
          </Heading>
          <Paragraph center={true}>
            Livet på hytta tilbringes gjerne i roligere tempo enn hverdagslivet
            hjemme. Sammenlignet med eiendomsmarkedet er det også ofte mindre
            hektisk ved salg av fritidseiendom, ettersom omsetningen av hytter i
            større grad følger årstidene. Å ta seg god tid kan lønne seg.
          </Paragraph>
        </HeroContent>
      </Hero>
      <Container style={pageFadeIn}>
        <Section>
          <Paragraph center={true}>
            Eiendomsmegler Gustav J. Sædberg i PrivatMegleren Sædberg & Lian
            kjenner godt til hyttemarkedet i skjærgårde
          </Paragraph>
          <Paragraph center={true}>
            – Hyttene og skjærgården viser seg naturligvis fra sin beste side i
            perioden fra påsketider til høstferien. Slikt sett kan hytter ved
            sjøen oppleves som sesongvare. Fordi hytte ikke er noe man må ha,
            men gjerne vil ha, er min erfaring at mange tar seg god tid når de
            ser etter et sted å tilbringe fritiden i årene fremover, beskriver
            Gustav.
          </Paragraph>
          <Paragraph center={true}>
            Tiden en fritidseiendom ligger tilgjengelig i markedet kan variere.
            I de store hyttefeltene, der hyttene ofte er mer like hverandre, bør
            man være forberedt på at det kan ta lengre tid å gjennomføre et
            salg. En hytte med unik utforming og plassering, egenskaper som kan
            oppfattes som et knapphetsgode, er derimot ofte mer ettertraktet og
            selges på kortere tid.
          </Paragraph>
          <Paragraph center={true}>
            Pia M. Murland er eiendomsmegler i PrivatMegleren Lillehammer, og er
            spesialist på salg av hytter på fjellet. Hun legger til at det er
            flere årsaker til at hytter ofte ikke selges like raskt som
            eiendommer.
          </Paragraph>
          <Paragraph center={true}>
            – Å dra på visning av hytte på fjellet eller ved sjøen er for de
            fleste mer omfattende enn å gå på visning av en ny eiendom i samme
            by. På grunn av avstanden mellom hjemmet og hyttene man vil se på,
            planlegger gjerne kjøpere hyttevisninger med en lengre tidshorisont,
            forteller hun.
          </Paragraph>
          <ImageContainer>
            <img
              style={{ width: '100%' }}
              src="https://cdn.reeltime.no/pm_assets/kampanjer/leveranse/fritid/Artikkelbilde_3.jpg"
              alt="artikkel bilde"
              className="artikkelbilde_1"
            />
          </ImageContainer>
          <Heading tag="h3" center={true}>
            Prisgitt meglers lokalkunnskap
          </Heading>
          <Paragraph center={true}>
            En attraktiv hytte med god beliggenhet, og til riktig pris, kan gi
            svært mange interessenter – på samme vis som med eiendommer. Det å
            prissette en fritidseiendom riktig kan imidlertid være en
            utfordring, ettersom hyttekjøpere gjerne er åpne for å se etter
            hytte i flere områder samtidig. Meglers kjennskap til{' '}
            <a href="https://privatmegleren.no/kampanje/fritidsartikkel">
              destinasjonen
            </a>{' '}
            og typiske hytteeiere i det området er vesentlig for å finne riktig
            markedsverdi.
          </Paragraph>
          <Paragraph center={true}>
            – Der eiendommen skal dekke noen praktiske behov i hverdagen, er
            hytta fristedet vårt. Da er det andre ting enn nærhet til skole og
            barnehage, jobb og matbutikk som teller mest i vurderingen.
            Kriteriene mange vurderer potensielle hytter etter er i mange
            tilfeller mer følelsesstyrt enn av praktisk art. Det påvirker også
            hvordan kjøpere oppfatter prisen, forklarer Pia.
          </Paragraph>
          <Paragraph center={true}>
            I PrivatMegleren bruker meglerne også databasen Eiendomsverdi for å
            vurdere riktig prisantydning basert på sammenliknbare data fra
            eiendommer som tidligere er solgt i området. Det omsettes imidlertid
            færre hytter enn eiendommer hvert år, derfor er meglers
            lokalkunnskap vel så viktig i denne delen av salgsprosessen.
          </Paragraph>
          <Paragraph center={true}>
            – I samråd med selger må vi bestemme oss for en prisstrategi, men
            samtidig være åpen for at det kan være nødvendig med en
            prisjustering underveis. Vi må være fleksible, og ta oss tid til å
            tilpasse oss tilbakemeldingene fra interessentene, sier Pia.
          </Paragraph>
          <Paragraph center={true}>
            Meglerne bruker også innsikt fra det avanserte annonseringsverktøyet
            KLiKK X i arbeidet med å justere kursen underveis i hyttesalget.
            Verktøyet gir fortløpende oversikt over hvordan salgsannonsen blir
            mottatt blant interessentene, slik at eksempelvis bilder, tekst og
            pris kan endres for å treffe målgruppene enda bedre for å oppnå et
            best mulig salg.
          </Paragraph>

          <Heading tag="h3" center={true}>
            Bistår med å realisere hyttedrømmen
          </Heading>
          <Paragraph center={true}>
            Mange ser etter fritidseiendom i andre områder enn der de bor. Det
            innebærer at man i jakten på drømmehytta må være villig til å bruke
            en del tid på å reise til og fra visninger. For å bistå med å finne
            riktige eiendommer tilbyr PrivatMegleren tjenesten Kjøp, hvor man
            som kjøper setter bort deler eller så godt som hele arbeidet, til
            erfarne meglere.
          </Paragraph>
          <Paragraph center={true}>
            – Når man ikke selv er så godt kjent i området der man vurderer å
            kjøpe hytte, kan det være lurt å få hjelp av de som kjenner området
            aller best. Som meglere kan vi bistå med å finne gode alternativer
            basert på dine ønsker for beliggenhet, pris og annet, og videre gi
            råd for hvordan du går frem i kjøpsprosessen, forklarer Gustav.
          </Paragraph>
          <Paragraph center={true}>
            Meglerens involvering i tjenesten Kjøp, varierer avhengig av behov.
            For noen er det tilstrekkelig å få en profesjonell part til å se
            over salgsoppgaven, for å avdekke om det er noe man må være spesielt
            oppmerksom på før man gir bud. For andre kan det være aktuelt å få
            bistand av en eiendomsmegler gjennom hele prosessen, fra å finne den
            riktige fritidseiendommen, til å gjennomføre budrunde og
            overtakelse.
          </Paragraph>
        </Section>
        <SelgeForm />
        {/*  <KjopeForm /> */}
      </Container>
    </>
  );
};

const FormWrapper = styled(animated.div)``;
const SelgeForm: React.FC = () => {
  const { routeConfig }: any = React.useContext(SiteContext);
  const [posted, setPosted] = React.useState(false);
  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const { submitWithDigger } = useDigtectiveContext();

  const [lead, { error, loading }] = useMutation(GENERAL_VALUATION_MUTATION, {
    onCompleted: ({ lead }) => {
      if (lead.valuation?.success) {
        setPosted(true);

        // GTM tracking

        createGTMEvent({
          event: 'pmFormSubmission',
          gaEvent: 'RT_VALUATION',
          gaCategory: 'RT_VALUATION',
          gaAction: 'RT_SEND_VALUATION'
        });
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Eks. Ola Nordmann',
        value: '',
        label: 'Navn *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Postnummer *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        lead({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: LeadType.VERDIVURDERING,
              pageType: PageType.GENERELL
            }
          }
        });
      });
    }
  });

  return (
    <>
      <FormWrapper style={fadeOut}>
        <Section className="form-section" style={{ paddingBottom: '10px' }}>
          <Heading tag="h1" center={true}>
            Bli kontaktet
          </Heading>
          <Paragraph center={true} className="form-text">
            Ønsker du å vite mer om hvordan vi kan &nbsp; hjelpe deg?
          </Paragraph>
          <Paragraph center={true} className="form-text">
            Fyll ut skjemaet og en av våre meglere i ditt nærområde kontakt.
          </Paragraph>
        </Section>
        <Form
          onSubmit={handleSubmit}
          noValidate
          style={{ paddingBottom: '50px' }}
        >
          {error &&
            error.graphQLErrors.map((error, index) => (
              <FormError key={`error_${index}`} message={error.message} />
            ))}
          {fields.map((item, index) => {
            return (
              <FormRow key={`field_${index}`}>
                <Input
                  type={item?.type}
                  name={item?.name}
                  label={item?.label}
                  labelAfter={item?.labelAfter}
                  placeholder={item?.placeholder}
                  value={item?.value}
                  onChange={handleChange}
                  error={item?.error}
                />
              </FormRow>
            );
          })}
          <ButtonGroup>
            <Button
              type="primary"
              colorTheme="gold"
              disabled={loading}
              loading={loading}
            >
              Kontakt meg
            </Button>
            <ButtonLink
              as="a"
              href={routeConfig.personvern.url}
              className="privacy-link-mobile"
            >
              Personvernpolicy
            </ButtonLink>
          </ButtonGroup>
        </Form>
      </FormWrapper>
      <Thanks style={fadeIn}>
        <Section className="form-section">
          <Heading tag="h1" center={true}>
            Tusen takk
          </Heading>
          <Paragraph center={true}>
            En eiendomsmegler vil ta kontakt med deg så snart som mulig.
          </Paragraph>
        </Section>
      </Thanks>
    </>
  );
};

export default FritidsArtikkel3;

const HeroContent = styled(animated.div)`
  display: flex;
  flex-flow: column;
  align-items: center;
  width: 100%;
  max-width: 960px;
  padding: 0 30px;

  h1 {
    margin-bottom: 0;
    width: 100%;
    word-break: break-word;
  }

  h2 {
    width: 100%;
    word-break: break-word;
  }
`;

const ImageContainer = styled.div`
  img  {
    object-fit: contain;
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: 250px;
  }

  @media all and (min-width: 600px) {
    img {
      object-fit: contain;
      display: block;
      margin-left: auto;
      margin-right: auto;
      width: 50%;
      padding: 1em 0 1em;
    }
  }
`;

/* const StyledTop = styled(animated.div)`
  margin-top: 66px;
  width: 100%;
  overflow: hidden;
  margin-bottom: 3em;

  img {
    width: 100%;
    max-height: 500px;
    object-fit: contain;
    object-position: center;
  }

  .video-mobil {
    display: block;
  }
  .video-desktop {
    display: none;
  }

  @media (min-width: 600px) {
    .video-mobil {
      display: none;
    }
    .video-desktop {
      display: block;
    }
  }

  @media all and (max-width: 1280px) {
    img {
      min-height: 300px;
      object-fit: cover;
    }
  }
`; */
